/**
 * Why a sign-in was refused, as far as it is safe to say.
 *
 * Supabase answers in English and the raw message surfaces as a toast, which
 * on dev reads "Email not confirmed" to a German landlord who then retypes a
 * password that was never wrong. `mailer_autoconfirm` is off there, so the
 * unconfirmed address is the COMMON failure, not an edge case.
 *
 * Deliberately three outcomes and not four: "invalid_credentials" covers a
 * wrong password AND an address with no account, and keeping them apart would
 * turn this form into a way to ask which e-mails are registered.
 *
 * Matched on `code` first and the English message second — older
 * supabase-js errors carry no `code`, and this has to keep working on both.
 */
export type SignInFailure = "unconfirmed" | "rate_limited" | "credentials";

export function classifySignInError(error: unknown): SignInFailure {
  const e = (error ?? {}) as {
    code?: unknown;
    status?: unknown;
    message?: unknown;
  };
  const code = typeof e.code === "string" ? e.code : "";
  const message = typeof e.message === "string" ? e.message.toLowerCase() : "";
  const status = typeof e.status === "number" ? e.status : 0;

  if (code === "email_not_confirmed" || message.includes("not confirmed")) {
    return "unconfirmed";
  }
  // `over_request_rate_limit`, `over_email_send_rate_limit`, and whatever the
  // next one is called — the prefix and the status code both hold.
  if (
    code.startsWith("over_") ||
    status === 429 ||
    message.includes("rate limit")
  ) {
    return "rate_limited";
  }
  return "credentials";
}
